import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { useForm, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { MenuItem, Select, Typography } from "@mui/material";
import { DatePicker } from "../shared/DatePicker";
import * as FormDialog from "../shared/FormDialog";
import type { Agreement, CreateOrUpdateAgreementPayload, GetPartnerByIdResponse } from "../api/partners";
import { AGREEMENT_TYPES, createOrUpdateAgreementMutationOptions } from "../api/partners";
import { useMutation } from "@tanstack/react-query";
import { hasErrorCode } from "../api/errors";
import type { DocumentSourceFormValues } from "../shared/DocumentSourceFields";
import {
  DocumentSourceFields,
  toDocumentSourcePayload,
  toDocumentSourceFormValues,
} from "../shared/DocumentSourceFields";
import { FileField } from "../shared/FileField";

type AgreementFormValues = DocumentSourceFormValues & {
  partnerId: string;
  title: string;
  type: Agreement["type"];
  validUntil?: string;
};

const toFormValues = ({ title, type, validUntil, ...source }: Agreement): Omit<AgreementFormValues, "partnerId"> => ({
  title,
  type,
  validUntil,
  ...toDocumentSourceFormValues(source),
});

const toPayload = ({ title, type, validUntil, ...source }: AgreementFormValues): CreateOrUpdateAgreementPayload => ({
  title,
  type,
  validUntil,
  ...toDocumentSourcePayload(source),
});

const AGREEMENT_TYPE_LABEL_KEYS: Record<Agreement["type"], TranslationKey> = {
  processorAgreementPartnerActsAsProcessor: "Partner.AgreementType.ProcessorAgreementPartnerActsAsProcessor",
  processorAgreementWeActAsProcessor: "Partner.AgreementType.ProcessorAgreementWeActAsProcessor",
  subProcessorAgreementWeActAsProcessor: "Partner.AgreementType.SubProcessorAgreementWeActAsProcessor",
  systemProviderAgreement: "Partner.AgreementType.SystemProviderAgreement",
  jointControllerAgreement: "Partner.AgreementType.JointControllerAgreement",
  recipientAgreement: "Partner.AgreementType.RecipientAgreement",
};

const SelectPlaceholder = ({ children }: { children: ReactNode }) => (
  <Typography component="span" sx={{ color: theme => theme.palette.text.placeholder }}>
    {children}
  </Typography>
);

type SavedAgreement = { agreementId: string; partnerId: string };

type CreateAndUpdateAgreementFormDialogProps = {
  onClose: () => void;
  onSaved?: (agreementId: string, partnerId: string) => void | Promise<unknown>;
  showTypeField?: boolean;
  description?: ReactNode;
  title?: string;
} & (
  | { partnerId: string; partners?: undefined }
  | { partnerId?: undefined; partners: Array<Pick<GetPartnerByIdResponse, "id" | "name">> }
) &
  (
    | { agreement: Agreement; defaultValues?: undefined }
    | { agreement?: undefined; defaultValues?: Pick<AgreementFormValues, "type"> }
  );

export const CreateAndUpdateAgreementFormDialog = ({
  onClose,
  defaultValues,
  agreement,
  partnerId,
  partners,
  onSaved,
  showTypeField = false,
  description,
  title,
}: CreateAndUpdateAgreementFormDialogProps) => {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const savedAgreementRef = useRef<SavedAgreement | null>(null);
  const isAgreementCreated = savedAgreementRef.current !== null;
  const onlyPartnerId = partners?.length === 1 ? partners[0]?.id : undefined;
  const initialPartnerId = partnerId ?? onlyPartnerId ?? "";

  const agreementId = agreement?.id;

  const { control, setFocus, setValue, handleSubmit } = useForm<AgreementFormValues>({
    defaultValues: agreement
      ? { ...toFormValues(agreement), partnerId: initialPartnerId }
      : { title: "", kind: "link", url: "", file: null, ...defaultValues, partnerId: initialPartnerId },
  });
  const selectedPartnerId = useWatch({ control, name: "partnerId" });
  const createOrUpdateAgreementMutation = useMutation(
    createOrUpdateAgreementMutationOptions({ partnerId: selectedPartnerId, agreementId })
  );
  const selectAgreementMutation = useMutation({
    mutationFn: async ({ agreementId: savedAgreementId, partnerId: savedPartnerId }: SavedAgreement) => {
      await onSaved?.(savedAgreementId, savedPartnerId);
    },
  });
  const isSubmitting = createOrUpdateAgreementMutation.isPending || selectAgreementMutation.isPending;

  FormDialog.useAutoFocusField(setFocus, "title");

  const requestClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const saveAgreementOnce = async (savedPartnerId: string, payload: CreateOrUpdateAgreementPayload) => {
    if (isAgreementCreated) {
      return true;
    }
    const saved = await createOrUpdateAgreementMutation.mutateAsync(payload).catch(() => undefined);
    if (!saved) {
      return false;
    }
    const savedAgreementId = agreementId ?? saved.agreementDocuments.at(-1)?.id;
    if (savedAgreementId) {
      savedAgreementRef.current = { agreementId: savedAgreementId, partnerId: savedPartnerId };
    }
    return true;
  };

  const defaultTitle = agreementId
    ? t("Partner.Agreement.Dialog.EditTitle")
    : t("Partner.Agreement.Dialog.CreateTitle");

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{title ?? defaultTitle}</FormDialog.Title>

      <FormDialog.Form
        onKeyDown={event => {
          if (event.key === "Escape" && !isDatePickerOpen) {
            event.stopPropagation();
            requestClose();
          }
        }}
        onSubmit={handleSubmit(async values => {
          selectAgreementMutation.reset();
          const isAgreementSaved = await saveAgreementOnce(values.partnerId, toPayload(values));
          if (!isAgreementSaved) {
            return;
          }
          if (savedAgreementRef.current && onSaved) {
            const isAgreementSelected = await selectAgreementMutation
              .mutateAsync(savedAgreementRef.current)
              .then(() => true)
              .catch(() => false);
            if (!isAgreementSelected) {
              return;
            }
          }
          onClose();
        })}
      >
        <FormDialog.Content>
          {description && (
            <Typography sx={{ pb: 2, fontSize: theme => theme.typography.pxToRem(14) }}>{description}</Typography>
          )}
          <FormDialog.FormError
            isError={createOrUpdateAgreementMutation.isError || selectAgreementMutation.isError}
            errorMessage={
              hasErrorCode(createOrUpdateAgreementMutation.error, "AGREEMENT_DOCUMENT_FILE_INVALID")
                ? t("Document.Error.FileNotSaved")
                : undefined
            }
          />

          <FormDialog.Fields>
            {partners && (
              <FormDialog.ControlledField
                control={control}
                name="partnerId"
                rules={{ required: t("Partner.Agreement.Dialog.PartnerRequired") }}
                label={t("Partner.Agreement.Dialog.PartnerLabel")}
              >
                {({ field, fieldState, id }) => (
                  <Select
                    {...field}
                    onChange={event => {
                      field.onChange(event);
                      setValue("file", null);
                    }}
                    id={id}
                    size="small"
                    fullWidth
                    displayEmpty
                    disabled={isAgreementCreated}
                    error={Boolean(fieldState.error)}
                    renderValue={value =>
                      partners.find(partner => partner.id === value)?.name ?? (
                        <SelectPlaceholder>{t("Partner.Agreement.Dialog.PartnerPlaceholder")}</SelectPlaceholder>
                      )
                    }
                  >
                    {partners.map(partner => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormDialog.ControlledField>
            )}

            <FormDialog.TextField
              control={control}
              name="title"
              rules={{ required: t("Partner.Agreement.Dialog.TitleRequired") }}
              label={t("Partner.Agreement.Dialog.TitleLabel")}
              placeholder={t("Partner.Agreement.Dialog.TitlePlaceholder")}
              disabled={isAgreementCreated}
            />

            {showTypeField && (
              <FormDialog.ControlledField
                control={control}
                name="type"
                rules={{ required: t("Partner.Agreement.Dialog.TypeRequired") }}
                label={t("Partner.Agreement.Dialog.TypeLabel")}
              >
                {({ field, fieldState, id }) => (
                  <Select
                    {...field}
                    id={id}
                    size="small"
                    fullWidth
                    displayEmpty
                    value={field.value ?? ""}
                    disabled={isAgreementCreated}
                    error={Boolean(fieldState.error)}
                    renderValue={value =>
                      value ? (
                        t(AGREEMENT_TYPE_LABEL_KEYS[value])
                      ) : (
                        <SelectPlaceholder>{t("Partner.Agreement.Dialog.TypePlaceholder")}</SelectPlaceholder>
                      )
                    }
                  >
                    {AGREEMENT_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {t(AGREEMENT_TYPE_LABEL_KEYS[type])}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormDialog.ControlledField>
            )}

            <DocumentSourceFields
              control={control}
              name="kind"
              savedFileName={agreement?.kind === "file" ? agreement.fileName : undefined}
              disabled={isAgreementCreated}
              linkField={
                <FormDialog.TextField
                  control={control}
                  name="url"
                  rules={{
                    validate: url => url.trim() !== "" || t("Partner.Agreement.Dialog.UrlRequired"),
                  }}
                  label={t("Partner.Agreement.Dialog.UrlLabel")}
                  placeholder={t("Partner.Agreement.Dialog.UrlPlaceholder")}
                  disabled={isAgreementCreated}
                />
              }
              fileField={
                selectedPartnerId ? (
                  <FileField
                    key={selectedPartnerId}
                    control={control}
                    name="file"
                    fileOwner={{ type: "Partner", id: selectedPartnerId }}
                    disabled={isAgreementCreated}
                  />
                ) : undefined
              }
            />

            <FormDialog.ControlledField
              control={control}
              name="validUntil"
              label={t("Partner.Agreement.Dialog.ValidUntilLabel")}
            >
              {({ field: { ref, ...field }, fieldState, id }) => (
                <DatePicker
                  format="DD MMMM YYYY"
                  open={isDatePickerOpen}
                  onClose={() => setIsDatePickerOpen(false)}
                  closeOnSelect
                  slotProps={{
                    textField: {
                      ref,
                      id,
                      size: "small",
                      fullWidth: true,
                      error: Boolean(fieldState.error),
                      onFocus: e => {
                        e.target.blur();
                        setIsDatePickerOpen(true);
                      },
                      onClick: () => setIsDatePickerOpen(true),
                    },
                  }}
                  {...field}
                  disabled={isAgreementCreated}
                  onChange={value => field.onChange(value ? value.format("YYYY-MM-DD") : undefined)}
                  value={field.value ? dayjs(field.value) : null}
                />
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={requestClose} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>
            {t(agreementId ? "Partner.Agreement.Dialog.SaveButton" : "Partner.Agreement.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
